/**
 * Promise wrappers around the shared http client for the feedback endpoints.
 *
 * Rejections are ``Error`` instances whose ``detail`` carries the backend's
 * human-readable message, matching the billing API contract so callers can
 * surface ``err.detail`` directly in a toast.
 */
import { data_from_server_post, get_base_url, post_formdata_with_progress } from '@/shared/http/client';

const SEND_FAILED_MESSAGE = 'Could not send your feedback. Please try again.';
const UPLOAD_FAILED_MESSAGE = 'Could not upload the attachment.';

const toError = (detail, fallback) => {
    const message = typeof detail === 'string' && detail.trim() ? detail : fallback;
    const err = new Error(message);
    err.detail = message;
    return err;
};

/** Pull the ``detail`` string out of an error body (FastAPI shape or plain text). */
function detailFromResponse(data) {
    if (typeof data === 'string') return data;
    const detail = data?.detail ?? data?.message;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
        return detail.map((item) => item?.msg).filter(Boolean).join(' ');
    }
    return '';
}

/**
 * ``POST /feedback`` ``{category, sentiment, details, workspace}`` →
 * ``{task_id, url}``. User email and platform URL are added server-side.
 */
export function createFeedbackTask({ category, sentiment, details, workspace }) {
    return new Promise((resolve, reject) => {
        data_from_server_post(
            'feedback',
            {
                category,
                sentiment: sentiment ?? null,
                details,
                workspace: workspace ?? null,
            },
            resolve,
            (detail) => reject(toError(detail, SEND_FAILED_MESSAGE)),
        ).catch((e) => reject(toError(e?.detail || e?.message, SEND_FAILED_MESSAGE)));
    });
}

/**
 * ``POST /feedback/{task_id}/attachments`` multipart upload of one image →
 * ``{attachment_id, url}``. Resolves with the response body, rejects with an
 * ``Error`` carrying ``detail`` on a non-2xx status or network failure.
 */
export async function uploadFeedbackAttachment(taskId, file, { onUploadProgress } = {}) {
    const formData = new FormData();
    formData.append('attachment', file, file.name);
    const base = get_base_url(window.location.origin);
    let response;
    try {
        response = await post_formdata_with_progress(
            `${base}/feedback/${encodeURIComponent(taskId)}/attachments`,
            formData,
            { withCredentials: true, onUploadProgress },
        );
    } catch (e) {
        throw toError(e?.message, UPLOAD_FAILED_MESSAGE);
    }
    if (!response.ok) {
        throw toError(detailFromResponse(response.data), UPLOAD_FAILED_MESSAGE);
    }
    return response.data;
}
